import {
  FileTextOutlined,
  HomeOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  ThunderboltOutlined,
  UserOutlined
} from '@ant-design/icons';
import { Contact, PackageMinus, UserPlus2, UsersRound, ClipboardList, PackageSearch } from 'lucide-react';
import { lazy } from 'react';
import QuantityLimit from '../pages/QuantityLimit';
import { encodeId } from '../utils/idEncoder';
import ProductActivity from '../pages/ProductActivity';

const ForgotPassword = lazy(() => import('../pages/Auth/ForgotPassword'));
const Login = lazy(() => import('../pages/Auth/Login'));
const Register = lazy(() => import('../pages/Auth/Register'));
const ResetPassword = lazy(() => import('../pages/Auth/ResetPassword'));
const Catalogue = lazy(() => import('../pages/Catalogue'));
const CatalogueDetails = lazy(() => import('../pages/Catalogue/Details'));
const PublicCatalogue = lazy(() => import('../pages/Catalogue/Public'));
const Customers = lazy(() => import('../pages/Customers'));
const CustomerDetails = lazy(() => import('../pages/Customers/Details'));
const Invoices = lazy(() => import('../pages/Invoices'));
const Orders = lazy(() => import('../pages/Orders'));
const CreateOrder = lazy(() => import('../pages/Orders/Create'));
const Promotions = lazy(() => import('../pages/Promotions'));
const AddPromotion = lazy(() => import('../pages/Promotions/Add'));
const Users = lazy(() => import('../pages/Users'));
const AddUser = lazy(() => import('../pages/Users/Add'));
const EditUser = lazy(() => import('../pages/Users/Add'));
const Dashboard = lazy(() => import('./../pages/Dashboard/index'));
const Home = lazy(() => import('./../pages/Home/index'));
const Leads = lazy(() => import('./../pages/Leads/index'));
const AddLead = lazy(() => import('./../pages/Leads/Add/index'));
const AlternateUsers = lazy(() => import('../pages/Users/Alternate'));
const CallList = lazy(() => import('../pages/CallList'));
const PendingApprovals = lazy(() => import('../pages/Customers/PendingApprovals'));
const LeadRoutes = lazy(() => import('../pages/Leads/Routes'));
const Sales = lazy(() => import('../pages/Sales'));

export const ROLES = {
  SUPER_ADMIN: 0,
  ADMIN: 1,
  FIELD_SALES_REP: 2,
  TEAM_LEAD: 3,
  SALES_REP: 4,
  CUSTOMER: 5,
};

export const ROUTE_GROUPS = {
  reports: {
    name: 'Reports',
    icon: <ClipboardList size={14} />,
    menuOrder: 10,
  },
};

export const getRoutes = () => {
  return {
    home: {
      name: 'Home',
      path: '/',
      route: () => '/',
      element: Home,
      public: true,
      global: true,
    },
    register: {
      name: 'Register',
      path: '/auth/register',
      route: () => '/auth/register',
      element: Register,
      public: true,
    },
    login: {
      name: 'Login',
      path: '/auth/login',
      route: () => '/auth/login',
      element: Login,
      public: true,
    },
    forgot: {
      name: 'Forgot Password',
      path: '/auth/forgot-password',
      route: () => '/auth/forgot-password',
      element: ForgotPassword,
      public: true,
    },
    reset: {
      name: 'Reset Password',
      path: '/auth/reset-password',
      route: () => '/auth/reset-password',
      element: ResetPassword,
      public: true,
    },
    dashboard: {
      name: 'Dashboard',
      path: '/',
      route: () => '/',
      element: Dashboard,
      public: false,
      allowedRoles: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.FIELD_SALES_REP, ROLES.TEAM_LEAD, ROLES.SALES_REP],
      icon: <HomeOutlined />,
      menuOrder: 1
    },
    users: {
      name: 'Users',
      path: '/users',
      route: () => '/users',
      element: Users,
      public: false,
      icon: <UserOutlined />,
      allowedRoles: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
      menuOrder: 2
    },
    add_users: {
      name: 'Add Users',
      path: '/users/add',
      route: () => '/users/add',
      element: AddUser,
      public: false,
      allowedRoles: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    },
    user_details: {
      name: 'User Details',
      path: '/users/:userId',
      route: (userId) => `/users/${encodeId(userId)}`,
      element: EditUser,
      public: false,
      allowedRoles: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    },
    alternate_users: {
      name: 'Alternate Users',
      path: '/users/alternate',
      route: () => '/users/alternate',
      element: AlternateUsers,
      public: false,
      allowedRoles: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    },
    order: {
      name: 'Orders',
      path: '/orders',
      route: () => '/orders',
      element: Orders,
      public: false,
      allowedRoles: [
        ROLES.SUPER_ADMIN,
        ROLES.ADMIN,
        ROLES.TEAM_LEAD,
        ROLES.SALES_REP,
        ROLES.CUSTOMER,
        ROLES.FIELD_SALES_REP,
      ],
      icon: <ShoppingCartOutlined />,
      menuOrder: 5
    },
    create_order: {
      name: 'Create Order',
      path: '/orders/create',
      route: () => '/orders/create',
      element: CreateOrder,
      public: false,
      allowedRoles: [
        ROLES.SUPER_ADMIN,
        ROLES.ADMIN,
        ROLES.TEAM_LEAD,
        ROLES.SALES_REP,
        ROLES.CUSTOMER,
        ROLES.FIELD_SALES_REP,
      ],
    },
    order_details: {
      name: 'Order Details',
      path: '/orders/:orderId',
      route: (orderId) => `/orders/${encodeId(orderId)}`,
      element: CreateOrder,
      public: false,
      allowedRoles: [
        ROLES.SUPER_ADMIN,
        ROLES.ADMIN,
        ROLES.TEAM_LEAD,
        ROLES.SALES_REP,
        ROLES.CUSTOMER,
        ROLES.FIELD_SALES_REP,
      ],
    },
    public_catalogue: {
      name: 'Public Catalogue',
      path: '/catalogue',
      route: () => '/catalogue',
      element: PublicCatalogue,
      public: true,
      global: true,
    },
    catalogue: {
      name: 'Catalogue',
      path: '/catalogue',
      route: () => '/catalogue',
      element: Catalogue,
      public: false,
      allowedRoles: [
        ROLES.SUPER_ADMIN,
        ROLES.ADMIN,
        ROLES.TEAM_LEAD,
        ROLES.SALES_REP,
        ROLES.CUSTOMER,
        ROLES.FIELD_SALES_REP,
      ],
      icon: <ShopOutlined />,
      menuOrder: 4
    },
    catalogue_details: {
      name: 'Catalogue Details',
      path: '/catalogue/:productId',
      route: (productId) => `/catalogue/${encodeId(productId)}`,
      element: CatalogueDetails,
      global: true,
      allowedRoles: [
        ROLES.SUPER_ADMIN,
        ROLES.ADMIN,
        ROLES.TEAM_LEAD,
        ROLES.SALES_REP,
        ROLES.CUSTOMER,
        ROLES.FIELD_SALES_REP,
      ],
    },
    customers: {
      name: 'Customers',
      path: '/customers',
      route: () => '/customers',
      element: Customers,
      public: false,
      allowedRoles: [
        ROLES.SUPER_ADMIN,
        ROLES.ADMIN,
        ROLES.TEAM_LEAD,
        ROLES.SALES_REP,
        ROLES.FIELD_SALES_REP,
      ],
      icon: <UsersRound size={14} />,
      menuOrder: 3
    },
    customer_details: {
      name: 'Customer Details',
      path: '/customers/:customerId',
      route: (customerId) => `/customers/${encodeId(customerId)}`,
      element: CustomerDetails,
      public: false,
      allowedRoles: [
        ROLES.SUPER_ADMIN,
        ROLES.ADMIN,
        ROLES.TEAM_LEAD,
        ROLES.SALES_REP,
        ROLES.FIELD_SALES_REP,
      ],
    },
    pending_approvals: {
      name: 'Pending Approvals',
      path: '/customers/pending-approvals',
      route: () => '/customers/pending-approvals',
      element: PendingApprovals,
      public: false,
      allowedRoles: [
        ROLES.SUPER_ADMIN,
        ROLES.ADMIN,
      ],
    },
    leads: {
      name: 'Leads',
      path: '/leads',
      route: () => '/leads',
      element: Leads,
      public: false,
      allowedRoles: [
        ROLES.SUPER_ADMIN,
        ROLES.ADMIN,
        ROLES.TEAM_LEAD,
        ROLES.SALES_REP,
        ROLES.FIELD_SALES_REP,
      ],
      icon: <UserPlus2 size={14} />,
      menuOrder: 8
    },
    add_lead: {
      name: 'Add Lead',
      path: '/leads/add',
      route: () => '/leads/add',
      element: AddLead,
      public: false,
      allowedRoles: [
        ROLES.SUPER_ADMIN,
        ROLES.ADMIN,
        ROLES.TEAM_LEAD,
        ROLES.SALES_REP,
        ROLES.FIELD_SALES_REP,
      ],
    },
    lead_routes: {
      name: 'Routes',
      path: '/leads/routes',
      route: () => '/leads/routes',
      element: LeadRoutes,
      public: false,
      allowedRoles: [
        ROLES.SUPER_ADMIN,
        ROLES.ADMIN,
        ROLES.FIELD_SALES_REP,
        ROLES.TEAM_LEAD,
        ROLES.SALES_REP,
      ],
    },
    update_lead: {
      name: 'Update Lead',
      path: '/leads/:leadId',
      route: (leadId) => `/leads/${encodeId(leadId)}`,
      element: AddLead,
      public: false,
      allowedRoles: [
        ROLES.SUPER_ADMIN,
        ROLES.ADMIN,
        ROLES.TEAM_LEAD,
        ROLES.SALES_REP,
        ROLES.FIELD_SALES_REP,
      ],
    },
    invoice: {
      name: 'Invoices',
      path: '/invoices',
      route: () => '/invoices',
      element: Invoices,
      public: false,
      allowedRoles: [
        ROLES.SUPER_ADMIN,
        ROLES.ADMIN,
        ROLES.TEAM_LEAD,
        ROLES.SALES_REP,
        ROLES.CUSTOMER,
        ROLES.FIELD_SALES_REP,
      ],
      icon: <FileTextOutlined />,
      menuOrder: 7
    },
    invoice_details: {
      name: 'Invoices Details',
      path: '/invoices/:invoiceId',
      route: (invoiceId) => `/invoices/${encodeId(invoiceId)}`,
      element: CreateOrder,
      public: false,
      allowedRoles: [
        ROLES.SUPER_ADMIN,
        ROLES.ADMIN,
        ROLES.TEAM_LEAD,
        ROLES.SALES_REP,
        ROLES.CUSTOMER,
        ROLES.FIELD_SALES_REP,
      ],
    },
    call_list: {
      name: 'Call List',
      path: '/call-list',
      route: () => '/call-list',
      element: CallList,
      public: false,
      allowedRoles: [
        ROLES.SUPER_ADMIN,
        ROLES.ADMIN,
        ROLES.FIELD_SALES_REP,
        ROLES.TEAM_LEAD,
        ROLES.SALES_REP,
      ],
      icon: <Contact size={14} />,
      menuOrder: 6
    },
    product_activity: {
      name: 'Product Activity',
      path: '/product-activity',
      route: () => `/product-activity`,
      element: ProductActivity,
      public: false,
      allowedRoles: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.FIELD_SALES_REP],
      icon: <PackageSearch size={14} />,
      menuOrder: 9,
      group: 'reports',
    },
    sales: {
      name: 'Sales',
      path: '/sales',
      route: () => '/sales',
      element: Sales,
      public: false,
      allowedRoles: [
        ROLES.SUPER_ADMIN,
        ROLES.ADMIN,
        ROLES.TEAM_LEAD,
        ROLES.FIELD_SALES_REP,
      ],
      icon: <ClipboardList size={14} />,
      group: 'reports',
    },
    promotions: {
      name: 'Promotions',
      path: '/promotions',
      route: () => '/promotions',
      element: Promotions,
      public: false,
      allowedRoles: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
      icon: <ThunderboltOutlined />,
      menuOrder: 11,
      group: 'reports',
    },
    promotion_details: {
      name: 'Promotion Details',
      path: '/promotions/:promotionId',
      route: (promotionId) => `/promotions/${encodeId(promotionId)}`,
      element: AddPromotion,
      public: false,
      allowedRoles: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    },
    add_promotion: {
      name: 'Add Promotion',
      path: '/promotions/add',
      route: () => `/promotions/add`,
      element: AddPromotion,
      public: false,
      allowedRoles: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    },
    product_restrictions: {
      name: 'Quantity Limit',
      path: '/quantity-limit',
      route: () => `/quantity-limit`,
      element: QuantityLimit,
      public: false,
      allowedRoles: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
      icon: <PackageMinus size={14} />,
      menuOrder: 12,
      group: 'reports',
    },
  };
};
